import traverson from 'traverson';
import JsonHalAdapter from 'traverson-hal';
import AuthStore from 'stores/AuthStore';
import Utils from 'util/Utils';
import WebUtils from 'util/WebUtils';
import { checkToken } from './authApi';

traverson.registerMediaType(JsonHalAdapter.mediaType, JsonHalAdapter);
/* global Promise */
const API_CONFIG = {
  url: Utils.urlAPI(),
  auth: `${Utils.urlAPI()}/auth`,
};

class AppApi {
  mobUserInfo(mobUserId) {
    return this.postResource({ mobUserId }, 'userpublic/mobuserinfo');
  }

  loadDestinations() {
    return this.getResource('destination/listall');
  }

  loadDestination(destinationId) {
    return this.getResource(`destination/get/${destinationId}`);
  }

  createDestination(destination) {
    return this.putResource(destination, 'destination/create');
  }

  updateDestination(destination) {
    const path = ('destination/update');
    return this.putResource(destination, path);
  }

  loadCountries() {
    return this.getResource('country/list');
  }

  addCityRole(userId, rights) {
    return this.postResource(rights, `user/role/city/${userId}`);
  }

  addPartnerRole(userId, cityId, rights) {
    return this.postResource(rights, `user/role/partner/${userId}/${cityId}`);
  }

  addSalesPointRole(userId, cityId, partnerId, rights) {
    return this.postResource(rights, `user/role/salespoint/${userId}/${cityId}/${partnerId}`);
  }

  addHotelRole(userId, cityId, partnerId, rights) {
    return this.postResource(rights, `user/role/hotel/${userId}/${cityId}/${partnerId}`);
  }

  deleteCityRole(userId, cityId) {
    return this.deleteResource(`user/role/city/${userId}/${cityId}`);
  }

  deletePartnerRole(userId, cityId, partnerId) {
    return this.deleteResource(`user/role/partner/${userId}/${cityId}/${partnerId}`);
  }

  deleteSalesPointRole(userId, cityId, partnerId, salesPointId) {
    return this.deleteResource(`user/role/salespoint/${userId}/${cityId}/${partnerId}/${salesPointId}`);
  }

  deleteHotelRole(userId, cityId, partnerId, hotelId) {
    return this.deleteResource(`user/role/hotel/${userId}/${cityId}/${partnerId}/${hotelId}`);
  }

  getRates(rlist, objectId) {
    return this.postResource(rlist, `rate/list/${objectId}`).then((data) => data);
  }

  getUsers(rlist) {
    return this.postResource(rlist, 'user/list').then((data) => data);
  }

  getUser(userId, partnerId, type, objectId) {
    let url = `user/getedit/${userId}`;
    if (userId === 'new') {
      url = `user/getnew/${partnerId}/${type}/${objectId}`;
    }
    return this.getResource(url).then((data) => data);
  }

  closeTempAccess(data) {
    return this.postResource(data, 'user/closetempaccess').then((data) => data);
  }

  getUserView(userId) {
    return this.getResource(`user/getview/${userId}`).then((data) => data);
  }

  updateUser(data) {
    return this.postResource(data, 'user/update').then((data) => data);
  }

  getUserDestinations(userId, allavail) {
    return this.getResource(`user/roledest/${userId}/${allavail}`).then((data) => data);
  }

  getCheckPartnerUserInvitation(partnerId) {
    return this.getResource(`user/checkpartneruserinvitation/${partnerId}`).then((data) => data);
  }

  getUserPartners(userId, cityId, allavail) {
    return this.getResource(`user/rolepartner/${userId}/${cityId}/${allavail}`).then((data) => data);
  }

  getUserBusiness(userId, partnerId) {
    return this.getResource(`user/rolebusiness/${userId}/${partnerId}`).then((data) => data);
  }

  getUserHotels(userId, partnerId) {
    return this.getResource(`user/rolehotel/${userId}/${partnerId}`).then((data) => data);
  }

  getListuserstay(partnerId, rlist) {
    return this.postResource(rlist, `user/listuserstay/${partnerId}`).then((data) => data);
  }

  getListclicks(partnerId, rlist) {
    return this.postResource(rlist, `ads/listclicks/${partnerId}`).then((data) => data);
  }

  getListTrans(rlist) {
    return this.postResource(rlist, 'admin/listtrans').then((data) => data);
  }

  updateTrans(tstring) {
    return this.postResource(tstring, 'admin/updatetrans').then((data) => data);
  }

  deleteTrans(tstring) {
    return this.postResource(tstring, 'admin/deletetrans').then((data) => data);
  }

  loadAdminCommisions(year, month) {
    return this.getResource(`commision/admin/${year}/${month}`);
  }

  loadPartnerCommisions(year, month) {
    return this.getResource(`commision/partner/${year}/${month}`);
  }

  loadHotelCommisions(year, month) {
    return this.getResource(`commision/hotel/${year}/${month}`);
  }

  loadCommisionMonths() {
    return this.getResource('commision/months');
  }

  loadJournals(userId, rlist) {
    return this.postResource(rlist, `journal/list/${userId}`).then((data) => data);
  }

  loadJournal(journalId) {
    return this.getResource(`journal/get/${journalId}`)
      .then((data) => {
        const result = data;
        return result;
      })
      .catch(() => undefined);
  }

  setJournalAccess(journalId, cmd) {
    return this.getResource(`journal/access/${journalId}/${cmd}`).then((data) => data);
  }

  getPartners(rlist) {
    return this.postResource(rlist, 'partner/list').then((data) => data);
  }

  getPartner(partnerId) {
    return this.getResource(`partner/get/${partnerId}`).then((data) => data);
  }

  loadPartner(partnerId) {
    let url = `partner/getedit/${partnerId}`;
    if (partnerId === 'new') {
      url = 'partner/getnew';
    }
    return this.getResource(url).then((data) => data);
  }

  getPartnerView(partnerId) {
    return this.getResource(`partner/getview/${partnerId}`).then((data) => data);
  }

  deletePartner(partnerId) {
    return this.getResource(`partner/delete/${partnerId}`).then((data) => data);
  }

  getPartnerTerms(partnerId) {
    return this.getResource(`partner/terms/${partnerId}`).then((data) => data);
  }

  getPartnerTemplates() {
    return this.getResource('partner/template/list').then((data) => data);
  }

  loadTransactions(partnerId, rlist) {
    return this.postResource(rlist, `finance/transactions/${partnerId}`).then((data) => data);
  }

  loadReport(params) {
    return this.postResource(params, 'report').then((data) => data);
  }

  getReports() {
    return this.getResource('report/list').then((data) => data);
  }

  loadInvoices(partnerId, rlist) {
    return this.postResource(rlist, `finance/invoices/${partnerId}`).then((data) => data);
  }

  loadDestinationById(destinationId) {
    return this.getResource(`destination/getInfo/${destinationId}`)
      .then((data) => {
        const result = data;
        return result;
      })
      .catch(() => undefined);
  }

  createPartner(partner) {
    return this.postResource(partner, 'partner/add');
  }

  updatePartner(partner) {
    return this.putResource(partner, 'partner/update');
  }

  getPartnerBlackList(partnerId) {
    return this.getResource(`partner/blacklist/${partnerId}`).then((data) => data);
  }

  updateContextValues(partnerId, contextValues) {
    return this.putResource(contextValues, `partner/contextvalues/${partnerId}`);
  }

  updatePartnerBlackList(partnerId, rq) {
    return this.postResource(rq, `partner/update/blacklist/${partnerId}`).then((data) => data);
  }

  loadUiTheme(oid, curid) {
    if (curid) {
      return this.getResource(`business/uitheme/${oid}/${curid}`).then((data) => data);
    }
    return this.getResource(`business/uitheme/${oid}`).then((data) => data);
  }

  saveUiTheme(oid, uiTheme) {
    this.postResource(uiTheme, `business/uitheme/${oid}`);
  }

  loadSalesPoints(type, rlist) {
    return this.postResource(rlist, `business/list/${type}`).then((data) => data);
  }

  loadSalesPoint(pointId, type, parentId) {
    let url = `business/get/${type}/${pointId}`;
    if (pointId === 'new') {
      url = `business/getnew/${type}/${parentId}`;
    }
    return this.getResource(url).then((data) => data);
  }

  loadBusinessMainData(pointId, type) {
    const url = `business/getBusinessMainData/${type}/${pointId}`;
    return this.getResource(url).then((data) => data);
  }

  loadContact(poiId) {
    const url = `guide/poi/getContact/${poiId}`;
    return this.getResource(url).then((data) => data);
  }

  updateContact(data) {
    const url = `guide/poi/putContact/${data.poiId}`;
    return this.postResource(data.contact, url).then((data) => data);
  }

  createSalesPoint(point) {
    return this.postResource(point, 'business/add').then((data) => data);
  }

  updateSalesPoint(point) {
    return this.putResource(point, 'business/update').then((data) => data);
  }

  loadDestinationList(rlist) {
    return this.postResource(rlist, 'destination/list').then((data) => data);
  }

  deleteSalesPoint(pointId) {
    return this.getResource(`business/delete/${pointId}`).then((data) => data);
  }

  changepBusiness(businessId) {
    return this.getResource(`business/changep/${businessId}`).then((data) => data);
  }

  changepartner(rq) {
    return this.postResource(rq, 'business/changepartner').then((data) => data);
  }

  loadRoutes(destinationId) {
    const path = (`destination/getInfo/${destinationId}`);
    return this.getResource(path).then((data) => {
      const { destination } = data;
      const { hotel } = data;
      if (hotel) {
        destination.hotel = hotel;
        destination.hotelPoi = data.hotelPoi;
      }
      return destination;
    });
  }

  loadGuideRoutes(destinationId) {
    return this.getResource(`guide/route/get/${destinationId}`);
  }

  getSnodes(ownerType, ownerId) {
    let url = `tnode/list/${ownerType}`;
    if (ownerId) {
      url = `tnode/list/${ownerType}/${ownerId}`;
    }
    if (ownerType === 'PRS') {
      url = `tnode/cityprices/${ownerId}`;
    }
    return this.getResource(url).then((data) => data);
  }

  getParentNode(nodeId) {
    const url = `tnode/getparent/${nodeId}`;
    return this.getResource(url).then((data) => {
      if (!data.snode.parentId) data.snode.parentId = '';
      if (!data.snode.prntId) data.snode.prntId = '';
      return data;
    });
  }

  getSnode(nodeId, ownerType, ownerId) {
    let url = `tnode/get/${nodeId}`;
    if (nodeId === 'new') {
      url = `tnode/getnew/${ownerType}/${ownerId}`;
    }
    return this.getResource(url).then((data) => {
      if (!data.snode.parentId) data.snode.parentId = '';
      if (!data.snode.prntId) data.snode.prntId = '';
      return data;
    });
  }

  getSharewithState(ownerId) {
    const url = `tnode/sharewithstate/${ownerId}`;
    return this.getResource(url).then((data) => data);
  }

  saveSharewithState(ownerId, sharewithState) {
    const url = `tnode/sharewithstate/${ownerId}`;
    return this.postResource({ sharewithState }, url).then((data) => data);
  }

  getCpNodeTree(ownerId, nodeId) {
    let url = `tnode/cptree/${ownerId}`;
    if (nodeId) url += `/${nodeId}`;
    return this.getResource(url).then((data) => data);
  }

  getCustomNode(nodeId, width, height, previewMode) {
    const url = `tnode/customnode/${nodeId}/${width}-${height}/${previewMode}`;
    return this.getResource(url).then((data) => data);
  }

  getCustomNodeSettings(nodeId) {
    const url = `tnode/customnodesettings/${nodeId}`;
    return this.getResource(url).then((data) => data);
  }

  saveCustomNodeSettings(nodeId, state) {
    const url = `tnode/customnodesettings/${nodeId}`;
    return this.postResource({ state }, url).then((data) => data);
  }

  getNodeHelpParams(nodeId) {
    const url = `tnode/nodehelpparams/${nodeId}`;
    return this.getResource(url).then((data) => data);
  }

  getExtSnode(nodeId) {
    return this.getResource(`tnode/getext/${nodeId}`).then((data) => data);
  }

  getTNodeChildrenAmount(parentId) {
    return this.getResource(`tnode/childrenamount/${parentId}`).then((data) => data);
  }

  getLightSnode(lang, ownerType, destinationId) {
    const dest = (!destinationId ? 'null' : destinationId);
    return this.getResource(`tnode/lightlist/${lang}/${ownerType}/${dest}`).then((data) => data);
  }

  getLightSnodeFiltered(lang, ownerType, destinationId) {
    const dest = (!destinationId ? 'null' : destinationId);
    return this.getResource(`tnode/lightlistfiltered/${lang}/${ownerType}/${dest}`).then((data) => data);
  }

  postLightSnodeFiltered(lang, destinationIds = [], objectId = '') {
    return this.postResource({ destinationIds, objectId }, `tnode/lightlistfiltered/${lang}`).then((data) => data);
  }

  getLightSnodeBlFiltered(lang, ownerType, destinationId) {
    const dest = (!destinationId ? 'null' : destinationId);
    return this.getResource(`tnode/lightlistblfiltered/${lang}/${ownerType}/${dest}`).then((data) => data);
  }

  changeNodePStatus(nodeId) {
    return this.putResource({}, `tnode/changePStatus/${nodeId}`).then((data) => data);
  }

  putSnode(snode) {
    return this.putResource(snode, 'tnode/put').then((data) => data);
  }

  delSnode(nodeId) {
    return this.deleteResource(`tnode/delete/${nodeId}`).then((data) => data);
  }

  liftSnode(move, nodeId) {
    return this.putResource({}, `tnode/${move}/${nodeId}`).then((data) => data);
  }

  putPrice(ownerId, rq) {
    return this.postResource(rq, `destination/price/${ownerId}`).then((data) => data);
  }

  putDicnode(ownerId, rq) {
    return this.postResource(rq, `destination/dicnode/${ownerId}`).then((data) => data);
  }

  getPushNodeId(partnerId) {
    return this.getResource(`tnode/pushnodeid/${partnerId}`).then((data) => data);
  }

  loadRoute(objectId, routeId) {
    const path = (`guide/route/get/${objectId}/${routeId}`);
    return this.getResource(path).then((data) => data);
  }

  loadPoi(objectId, poiId) {
    const path = (`guide/poi/get/${objectId}/${poiId}`);
    return this.getResource(path).then((data) => data);
  }

  createPoi(poi) {
    return this.postResource(poi, 'guide/poi/create');
  }

  updatePoi(poi) {
    return this.putResource(poi, 'guide/poi/update');
  }

  updatePoiLocation(id, location) {
    return this.putResource(location, `guide/poi/updatelocation/${id}`);
  }

  changePoiAreaType(id, type) {
    return this.putResource(type, `guide/poiarea/${id}/type`);
  }

  updatePoiAreaRadius(id, radius) {
    return this.putResource(radius, `guide/poiarea/${id}/radius`);
  }

  updatePoiAreaPath(id, path) {
    return this.putResource(path, `guide/poiarea/${id}/path`);
  }

  deletePoi(objectId, poiId) {
    return this.deleteResource(`guide/poi/delete/${objectId}/${poiId}`);
  }

  getPois(type, objectId) {
    return this.getResource(`/guide/poi/list/${type}/${objectId}`).then((data) => data);
  }

  createRoute(route) {
    return this.postResource(route, 'guide/route/create');
  }

  updateRoute(route) {
    return this.putResource(route, 'guide/route/update');
  }

  deleteRoute(objectId, routeId) {
    return this.deleteResource(`guide/route/delete/${objectId}/${routeId}`);
  }

  deleteWaypoint(routeId, index, objectId) {
    return this.deleteResource(`guide/guidepoint/${objectId}/${routeId}/${index}`);
  }

  setStartWaypoint(routeId, index, objectId) {
    return this.postResource({}, `guide/guidepoint/setstartpoint/${objectId}/${routeId}/${index}`);
  }

  createWaypoint(routeId, waypoint, objectId) {
    return this.postResource(waypoint, `guide/guidepoint/${objectId}/${routeId}`);
  }

  waypointInsert(routeId, index, waypoint, objectId) {
    return this.putResource(waypoint, `guide/guidepoint/insert/${objectId}/${routeId}/${index}`);
  }

  waypointRelocate(routeId, index, waypoint, objectId) {
    return this.putResource(waypoint, `guide/guidepoint/relocate/${objectId}/${routeId}/${index}`);
  }

  replaceWaypoints(objectId, routeId, waypoints) {
    return this.postResource(waypoints, `guide/guidepoint/replacewaypoints/${objectId}/${routeId}`);
  }

  login(authData) {
    return this.postResource(authData, 'auth/loginViaEmail').then((data) => data);
  }

  loginViaAccesslink(accessLink) {
    return this.postResource({ accessLink }, 'auth/loginViaAccesslink').then((data) => data);
  }

  loginViaFirebaseMpa(authData) {
    return this.postResource({ ...authData, domain: Utils.isMpa() ? 'myperfectapp' : 'mypersonalguide' }, 'auth/loginViaFirebaseMpa').then((data) => data);
  }

  loginViaAppleMpa(authData) {
    const { tempKey, ...appleAuthData } = authData;
    appleAuthData.socialToken = tempKey; // since java class AuthForm has no field tempKey
    return this.postResource({ ...appleAuthData, domain: Utils.isMpa() ? 'myperfectapp' : 'mypersonalguide' }, 'auth/loginViaAppleMpaComplete').then((data) => data);
  }

  registerViaFirebaseMpa(authData, salesRef) {
    const mobUserId = localStorage.getItem('mobUserId');
    if (mobUserId) authData.mobUserId = mobUserId;
    const url = salesRef ? `userpublic/registerViaFirebaseMpa?salesRef=${salesRef}` : 'userpublic/registerViaFirebaseMpa';
    return this.postResource({ ...authData, domain: Utils.isMpa() ? 'myperfectapp' : 'mypersonalguide' }, url).then((data) => data);
  }

  registerViaAppleMpa(tempKey, salesRef) {
    const mobUserId = localStorage.getItem('mobUserId');
    const url = 'auth/registerViaAppleMpaComplete';
    return this.postResource({
      tempKey, salesRef, mobUserId, domain: Utils.isMpa() ? 'myperfectapp' : 'mypersonalguide',
    }, url).then((data) => data);
  }

  logout(mobUserId) {
    const url = `auth/logout${!!mobUserId && mobUserId !== '' ? `?mobUserId=${mobUserId}` : ''}`;
    return this.getResource(url).then((data) => data);
  }

  // resource
  my() {
    return this.getResource('admin/my').then((data) => data);
  }

  forget(fgtData) {
    return this.postResource(fgtData, 'auth/forgotFromWeb').then((data) => data);
  }

  forgotPassword(email) {
    return this.postResource({ email }, 'auth/forgotFromWebNew').then((data) => data);
  }

  newPassword(password) {
    return this.postResource({ password }, 'user/password').then((data) => data);
  }

  checkUserTempKey(tempKey) {
    return this.postResource({ tempKey }, 'userpublic/checktempkey').then((data) => data);
  }

  contact(formData) {
    return this.postResource(formData, 'site/contact').then((data) => data);
  }

  activateDeviceGet() {
    return this.getResource('admin/activateget').then((data) => data);
  }

  activateDevice(device) {
    return this.postResource(device, 'admin/activate').then((data) => data);
  }

  getContract(contractId, partnerId) {
    let url = `partner/contract/get/${contractId}`;
    if (contractId === 'new') {
      url = `partner/contract/getnew/${partnerId}`;
    }
    return this.getResource(url).then((data) => data);
  }

  getCpContractFinance(partnerId, salesKey) {
    if (salesKey) {
      return this.getResource(`salescontract/cpcontractfinance/${partnerId}/${salesKey}`).then((data) => data);
    }
    return this.getResource(`partner/cpcontractfinance/${partnerId}`).then((data) => data);
  }

  saveCpContractFinance(partnerId, finance, salesKey) {
    if (salesKey) {
      return this.putResource(finance, `partner/cpcontractfinance/${partnerId}/${salesKey}`, finance).then((data) => data);
    }
    return this.putResource(finance, `partner/cpcontractfinance/${partnerId}`, finance).then((data) => data);
  }

  signCpContract(partnerId) {
    return this.postResource({}, `partner/cpcontractsign/${partnerId}`).then((data) => data);
  }

  acceptContract(partnerId) {
    return this.postResource({}, `partner/contractaccept/${partnerId}`).then((data) => data);
  }

  inviteCpUser(partnerId) {
    return this.postResource({}, `partner/cpuserinvite/${partnerId}`).then((data) => data);
  }

  updateContract(contract) {
    return this.postResource(contract, 'partner/contract/update').then((data) => data);
  }

  getContracts(rlist) {
    return this.postResource(rlist, 'partner/contract/list').then((data) => data);
  }

  getAdsCampaign(campaignId, partnerId) {
    let url = `ads/get/${campaignId}`;
    if (campaignId === 'new') {
      url = `ads/getnew/${partnerId}`;
    }
    return this.getResource(url).then((data) => data);
  }

  getAdsCampaigns(partnerId, rlist) {
    return this.postResource(rlist, `ads/list/${partnerId}`).then((data) => data);
  }

  updateAdsCampaign(campaign) {
    return this.postResource(campaign, 'ads/update').then((data) => data);
  }

  gopay(payType, partnerId) {
    return this.getResource(`finance/gopay/${payType}/${partnerId}`).then((data) => data);
  }

  addccstart(partnerId) {
    return this.getResource(`finance/addccstart/${partnerId}`).then((data) => data);
  }

  getPaymentLink(partnerId, s, cur) {
    return this.getResource(`finance/paystart/${partnerId}/${s}/${cur}`).then((data) => data);
  }

  getPartnerBudget() {
    return this.getResource('partner/budget').then((data) => data);
  }

  addBudget(partnerId, s, cur) {
    return this.getResource(`finance/addbudget/${partnerId}/${s}/${cur}`).then((data) => data);
  }

  getIndex() {
    return this.getResource('site/index').then((data) => data);
  }

  getNodesStarts(keyStartsWith) {
    return this.getResource(`site/ndl/${keyStartsWith}`).then((data) => data);
  }

  getNode(path) {
    return this.getResource(`site/nd/${path}`).then((data) => data);
  }

  getMediaFiles(ownerType, ownerId) {
    return this.getResource(`media/mediapage/${ownerType}/${ownerId}`).then((data) => data);
  }

  delAttFiles(ownerType, ownerId, rq) {
    return this.postResource(rq, `media/deldata/${ownerType}/${ownerId}`).then((data) => data);
  }

  putAttFile(ownerType, ownerId, att) {
    return this.postResource(att, `media/putatt/${ownerType}/${ownerId}`).then((data) => data);
  }

  getWifiproviders(rlist) {
    return this.postResource(rlist, 'admin/wifilist').then((data) => data);
  }

  getWifiProvider(providerId) {
    return this.getResource(`admin/wifiget/${providerId}`).then((data) => data);
  }

  putWifiProvider(wifiProvider) {
    return this.postResource(wifiProvider, 'admin/wifiupdate').then((data) => data);
  }

  deleteWifiProvider(providerId) {
    return this.getResource(`admin/wifidelete/${providerId}`).then((data) => data);
  }

  getListHotels() {
    return this.getResource(AuthStore.isLogged() ? 'datapub/listhotelsSecured' : 'datapub/listhotels').then((data) => data);
  }

  dataPub(objRq) {
    return this.postResource(objRq, 'datapub/upd').then((data) => data);
  }

  getSpeList(destinationId) {
    return this.getResource(`tnode/listspe/${destinationId}`);
  }

  getSpeCategories(lang, destinationIds) {
    return this.postResource({ destinationIds, type: 'SPE' }, `tnode/lightlistfiltered/${lang}`);
  }

  getAdsList(partnerId, type, landing, status) {
    return this.postResource({
      partnerId, type, landing, status,
    }, 'ads/lightlist');
  }

  getSalesHotels(params) {
    return this.postResource(params || {}, 'business/lightlist/htl');
  }

  getSalesAdv(params) {
    return this.postResource(params || {}, 'business/lightlist/buz');
  }

  getAdvParents(destinationId) {
    return this.getResource(`tnode/listbusiness/${destinationId}`);
  }

  getCCStart(cardInfo) {
    return this.postResource(cardInfo, 'finance/addccstart');
  }

  getCCInfo(partnerId) {
    return this.getResource(`finance/getccdata${partnerId ? `/${partnerId}` : ''}`);
  }

  setCCBudget(budget) {
    return this.getResource(`finance/setccbudget/${budget}`);
  }

  financeStartPayment(payment) {
    return this.postResource(payment, 'finance/paystart');
  }

  getSaleUsers(searchParams) {
    return this.postResource(searchParams, 'user/saleslist');
  }

  createSaleUser(userInfo) {
    return this.postResource(userInfo, 'user/createsalesuser');
  }

  saveAndInviteSaleUser(userInfo) {
    return this.postResource(userInfo, 'user/saveandinvitesalesuser');
  }

  getSaleUser(id) {
    return this.getResource(`user/getsalesuser/${id}`);
  }

  getSaleUserEmail(salesKey) {
    return this.getResource(`datapub/getsalesuseremail/${salesKey}`);
  }

  getSalesNotes(filter) {
    return this.postResource(filter, 'sales/companynotes');
  }

  getSalesNote(id) {
    return this.getResource(`sales/companynote/${id}`);
  }

  saveSalesNote(note) {
    return this.postResource(note, 'sales/companynote');
  }

  getInvoices(searchParams) {
    return this.postResource(searchParams, 'finance/invoices');
  }

  prepareInvoice(params) {
    return this.postResource(params, 'finance/prepareinvoice');
  }

  payInvoice(params) {
    return this.postResource(params, 'finance/payinvoice');
  }

  getAdsFinance(id) {
    // Adv, Mrk => id=campaignId
    // Poi      => id=poiId
    return this.getResource(`ads/getfinance/${id}`);
  }

  putAdsFinance(info) {
    // Adv, Mrk => id=campaignId
    // Poi      => id=poiId
    return this.putResource(info, 'ads/savefinance');
  }

  prepareAdsFinance() {
    return this.getResource('ads/getnewlst');
  }

  getPartnerContact(partnerId, salesKey) {
    if (salesKey) {
      return this.getResource(`salescontract/contact/${partnerId}/${salesKey}`);
    }
    return this.getResource(`partner/contact/${partnerId}`);
  }

  getPartnerContactWithBilling(partnerId, salesKey) {
    return this.getResource(`salescontract/contactwithbilling/${partnerId}/${salesKey}`);
  }

  putPartnerContact(contact, salesKey, billingContact) {
    if (salesKey) {
      if (!billingContact) return this.putResource(contact, `salescontract/contact/${salesKey}`);
      return this.putResource({ contact, billingContact }, `salescontract/contactwithbilling/${salesKey}`);
    }
    return this.putResource(contact, 'partner/contact');
  }

  getPartnerContractList() {
    return this.getResource('partner/contract/list');
  }

  getNewContractInfo(partnerId) {
    return this.getResource(`partner/addcontract/info/${partnerId}`);
  }

  createAdvUser(userInfo) {
    return this.postResource(userInfo, 'user/createadvuser');
  }

  createSubappUser(nodeId, email) {
    return this.postResource({ nodeId, email }, 'user/createsubappuser');
  }

  getDestinationList({ lang = 'en' }) {
    return this.getResource(`datapub/destinations/${lang}`);
  }

  getCountries({ lang = 'en' }) {
    return this.getResource(`datapub/destinationsadvlist/${lang}`).then((data) => data);
  }

  getCountriesCp({ lang = 'en' }) {
    return this.getResource(`datapub/destinationscp/${lang}`).then((data) => data);
  }

  registerCp(user) {
    return this.postResource(user, 'userpublic/selfcreatecpuser');
  }

  registerAdv(user) {
    return this.postResource(user, 'userpublic/selfcreateadvuser');
  }

  confirmKeyAdv(tempKey) {
    return this.postResource({ tempKey }, 'userpublic/selfcreateadvusercomplete');
  }

  confirmInvitation(tempKey, password) {
    return this.postResource(
      { tempKey, password },
      'userpublic/createpartnerusercomplete',
    );
  }

  saveStripeAcc(paymentMethod) {
    return this.postResource(paymentMethod, 'partner/savestripecc');
  }

  getStripeRef() {
    return this.getResource('partner/getstriperef').then((data) => data);
  }

  getPartnerQuestions(hotelId, nodeId) {
    return this.getResource(`partner/userprofilequestions/${hotelId}/${nodeId || 0}`).then((data) => data);
  }

  putPartnerQuestions(hotelId, nodeId, questions) {
    return this.putResource(questions, `partner/userprofilequestions/${hotelId}/${nodeId || 0}`).then((data) => data);
  }

  getNodeProfileQuestions(nodeId) {
    return this.getResource(`tnode/userprofilequestions/${nodeId}`).then((data) => data);
  }

  putNodeProfileQuestions(nodeId, questions) {
    return this.putResource(questions, `tnode/userprofilequestions/${nodeId}`).then((data) => data);
  }

  async cpsalesPostKey(login) {
    const data = await this.postResource({ login }, 'auth/loginSales');
    return data;
  }

  async getNodeGrid(lang = 'en', nodeId, ownerId) {
    const data = await this.getResource(`ds/nodes/${lang}/${ownerId}/${nodeId}`);
    return data;
  }

  async getPoiGrid(lang = 'en', nodeId) {
    const data = await this.getResource(`ds/pois/${lang}/${nodeId}`);
    return data;
  }

  async gridNodeRelocate(oldNodeId, newNodeId) {
    // PUT /rest/tnode/relocate/{nodeId}/{targetNodeId}
    await this.putResource({}, `tnode/relocate/${oldNodeId}/${newNodeId}`);
  }

  async gridNodeChangeParent(action, id, parentId) {
    // POST /rest/tnode/changeParent
    await this.postResource({ action, id, parentId }, 'tnode/changeParent');
  }

  async postCustomNodeSection(nodeId, type) {
    const data = await this.postResource({ type }, `tnode/new/custom/${nodeId}`);
    return data;
  }

  // ------- UTIL ------

  getResource(path) {
    const nc = this.reqNoCache();
    const token = WebUtils.getCookie('token') || localStorage.getItem('token');
    return new Promise((resolve, reject) => {
      traverson
        .from(`${API_CONFIG.url}/${path}${nc}`)
        .withRequestOptions({
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
          // withCredentials: true,
        })
        .getResource((err, resource) => {
          if (err) {
            reject(err);
          } else {
            if (resource.statusCode === 403) {
              checkToken();
            }
            resolve(resource);
          }
        });
    });
  }

  postResource(params, path) {
    const token = WebUtils.getCookie('token') || localStorage.getItem('token');
    return new Promise((resolve, reject) => {
      traverson
        .from(`${API_CONFIG.url}/${path}`)
        .withRequestOptions({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          // withCredentials: true,
        })
        .post(params, (err, resource) => {
          if (err) {
            reject(err);
          } else {
            try {
              if (resource.statusCode === 403) {
                checkToken();
              }
              const resp = JSON.parse(resource.body);
              resolve(resp);
            } catch (exc) {
              reject('server error');
            }
          }
        });
    });
  }

  putResource(params, path) {
    const token = WebUtils.getCookie('token') || localStorage.getItem('token');
    return new Promise((resolve, reject) => {
      traverson
        .from(`${API_CONFIG.url}/${path}`)
        .json()
        .convertResponseToObject(true)
        .withRequestOptions({
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          // withCredentials: true,
        })
        .put(params, (err, resource) => {
          if (err) {
            reject(err);
          } else {
            if (resource.statusCode === 403) {
              checkToken();
            }
            resolve(resource);
          }
        });
    });
  }

  deleteResource(path) {
    const token = WebUtils.getCookie('token') || localStorage.getItem('token');
    return new Promise((resolve, reject) => {
      traverson
        .from(`${API_CONFIG.url}/${path}`)
        .withRequestOptions({
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
          // withCredentials: true,
        })
        .newRequest()
        .delete((err, resource) => {
          if (err) {
            reject(err);
          } else {
            if (resource.statusCode === 403) {
              checkToken();
            }
            const resp = JSON.parse(resource.body);
            resolve(resp);
          }
        });
    });
  }

  reqNoCache() {
    // if (BrowserDetect && BrowserDetect.browser === 'Explorer') {
    //  return `?t=${(new Date()).getTime()}`;
    // }
    return '';
  }

  getIDFromUrl(url, startForm) {
    const urlItems = url.substring(url.indexOf('/', startForm) + 1).split('/');
    return urlItems.find((item) => this.isID(item));
  }

  isID(id) {
    const re = /^[0-9A-Fa-f]{24}$/;
    return re.test(id);
  }
}

export default new AppApi();
